import React from 'react';
import './switch-button.scss';

interface SwitchProperties {
  /** The type of input element to render */
  type?: 'checkbox' | 'radio';
  /** The checked state of the switch */
  checked?: boolean;
  /** Callback function triggered when the switch state changes */
  change?: (checked: boolean) => void;
  fontSize?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

/**
 * A switch button component that toggles between checked and unchecked states.
 * Provides an accessible and customizable toggle switch interface that can be used
 * as either a checkbox or radio input.
 * @param {object} props - Component properties
 * @param {('checkbox'|'radio')} [props.type] - The type of input (checkbox or radio)
 * @param {boolean} [props.checked] - The initial checked state of the switch
 * @param {Function} [props.change] - Callback function that triggers on state change
 * @returns {JSX.Element} The rendered switch component
 * @example
 * ```tsx
 * <Switch
 *   type="checkbox"
 *   checked={isEnabled}
 *   change={(checked) => setIsEnabled(checked)}
 * />
 * ```
 */
function SwitchButton({
  type = 'checkbox',
  checked = false,
  change = () => {},
  fontSize,
  disabled,
}: SwitchProperties): JSX.Element {
  // Generate a unique ID for accessibility
  const inputId = `switch-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <label className={`switch ${fontSize}`} htmlFor={inputId}>
      <input
        id={inputId}
        type={type}
        checked={checked}
        onChange={(event) => !disabled && change(event.target.checked)}
        aria-label="Switch input"
        style={{ display: 'none' }}
      />
      <span className="slider" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }} />
    </label>
  );
}

/**
 * Default properties for the Switch component.
 * Provides default values for optional props to ensure component functionality
 * when not all props are specified.
 */
SwitchButton.defaultProps = {
  type: 'checkbox',
  checked: false,
  /**
   * Callback function triggered when the switch state changes.
   * This default is an empty function, but can be overridden by providing
   * a custom function in the `change` prop.
   */
  change: () => {},
  fontSize: 'large',
  disabled: false,
};

export default SwitchButton;
